import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Search, Crown, MapPin, Download, X, Wallet,
  TrendingUp, AlertTriangle, Target, Layers, Building2,
} from "lucide-react";

const TIER_STYLES = {
  platinum: { bg: "bg-slate-900", text: "text-white", label: "Platinum · 12%" },
  gold:     { bg: "bg-amber-100", text: "text-amber-900", label: "Gold · 9%" },
  silver:   { bg: "bg-slate-200", text: "text-slate-700", label: "Silver · 7%" },
  bronze:   { bg: "bg-orange-100", text: "text-orange-900", label: "Bronze · 5%" },
};

const fmt$ = (cents) => "$" + Number((cents || 0) / 100).toFixed(2);
const pct = (v) => Math.round((Number(v) || 0) * 100) + "%";

export default function AdminCoverage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("by_zip");

  useEffect(() => {
    if (user && user.role !== "platform_admin") nav("/dashboard");
  }, [user, nav]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => nav("/admin")} data-testid="coverage-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
            </Button>
            <div>
              <div className="font-display font-bold text-slate-900">Coverage Map</div>
              <div className="text-xs text-slate-500">Where companies are bidding · drives your traffic decisions</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 mb-5">
          <TabBtn id="by_zip"     active={tab} setActive={setTab} icon={MapPin}     label="By ZIP" />
          <TabBtn id="by_company" active={tab} setActive={setTab} icon={Building2}  label="By company" />
          <TabBtn id="demand"     active={tab} setActive={setTab} icon={Target}     label="Demand vs supply" />
        </div>

        {tab === "by_zip" && <ByZipTab />}
        {tab === "by_company" && <ByCompanyTab />}
        {tab === "demand" && <DemandTab />}
      </main>
    </div>
  );
}

function TabBtn({ id, active, setActive, icon: Icon, label }) {
  const on = id === active;
  return (
    <button
      onClick={() => setActive(id)}
      data-testid={`coverage-tab-${id}`}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-bold transition-colors ${on ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function Stat({ label, value, accent = "text-slate-900", sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function TierPill({ tier }) {
  const s = TIER_STYLES[tier] || TIER_STYLES.silver;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
      <Crown className="w-3 h-3" /> {s.label}
    </span>
  );
}

// ----------------- BY ZIP -----------------
function ByZipTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/coverage/by-zip")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load coverage"))
      .finally(() => setLoading(false));
  }, []);

  const rows = data?.zips || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (stateFilter && r.state !== stateFilter) return false;
      if (!q) return true;
      return (
        r.zip_code.includes(q) ||
        (r.city_name || "").toLowerCase().includes(q) ||
        r.bidders.some((b) => b.business_name.toLowerCase().includes(q))
      );
    });
  }, [rows, search, stateFilter]);

  const exportCsv = () => {
    if (!filtered.length) { toast.info("Nothing to export"); return; }
    const headers = ["zip", "state", "city", "bidder_count", "top_tier", "top_percent", "total_wallet_balance", "bidders"];
    const csv = [headers, ...filtered.map((r) => [
      r.zip_code, r.state, r.city_name, r.bidder_count, r.top_tier, (r.top_percent * 100).toFixed(0) + "%",
      "$" + ((r.total_wallet_balance_cents || 0) / 100).toFixed(2),
      r.bidders.map((b) => `${b.business_name} (${b.tier} ${(b.percent * 100).toFixed(0)}%)`).join(" | "),
    ])].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `coverage-by-zip-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="ZIPs with bidders" value={data?.total_zips ?? "—"} accent="text-[#C75D33]" />
        <Stat label="Active companies" value={data?.total_active_companies ?? "—"} />
        <Stat label="States covered" value={data?.states_covered?.length ?? "—"} sub={(data?.states_covered || []).slice(0, 4).join(", ")} />
        <Stat label="Top bid" value={rows[0] ? pct(rows[0].top_percent) : "—"} accent="text-emerald-600" />
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search ZIP, city, or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded-xl"
            data-testid="coverage-by-zip-search"
          />
        </div>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700"
          data-testid="coverage-by-zip-state-filter"
        >
          <option value="">All states</option>
          {(data?.states_covered || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <Button variant="outline" className="rounded-xl h-10" onClick={exportCsv} disabled={!filtered.length} data-testid="coverage-by-zip-export-btn">
          <Download className="w-4 h-4 mr-1.5" /> CSV
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">ZIP</th>
                <th className="text-left px-5 py-3 font-bold">City · State</th>
                <th className="text-center px-5 py-3 font-bold">Bidders</th>
                <th className="text-left px-5 py-3 font-bold">Top bid</th>
                <th className="text-left px-5 py-3 font-bold">Companies</th>
                <th className="text-right px-5 py-3 font-bold">Wallet pool</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading coverage…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-500" data-testid="coverage-by-zip-empty">
                  {rows.length === 0 ? "No marketplace bidders yet. Enable marketplace on at least one company to populate this." : "No matches for your filter."}
                </td></tr>
              ) : filtered.map((r) => (
                <tr key={r.zip_code} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`coverage-by-zip-row-${r.zip_code}`}>
                  <td className="px-5 py-3 font-mono font-bold text-slate-900">{r.zip_code}</td>
                  <td className="px-5 py-3 text-slate-700">
                    <div className="font-semibold">{r.city_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r.state || ""}</div>
                  </td>
                  <td className="px-5 py-3 text-center font-bold text-slate-900">{r.bidder_count}</td>
                  <td className="px-5 py-3"><TierPill tier={r.top_tier} /></td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {r.bidders.slice(0, 3).map((b) => (
                        <span key={b.company_id} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 max-w-[200px] truncate">
                          {b.business_name} · {pct(b.percent)}
                        </span>
                      ))}
                      {r.bidders.length > 3 && (
                        <span className="text-[11px] text-slate-500 font-semibold">+{r.bidders.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-bold tabular-nums">
                    <span className={r.any_wallet_funded ? "text-slate-900" : "text-rose-600"}>
                      {fmt$(r.total_wallet_balance_cents)}
                    </span>
                    {!r.any_wallet_funded && <div className="text-[10px] font-bold text-rose-600 uppercase">No wallets funded</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        Each ZIP listed has at least one active marketplace bidder. Top bid wins the lead automatically (Platinum &gt; Gold &gt; Silver &gt; Bronze) — focus paid traffic on the high-bid + funded-wallet ZIPs for the strongest unit economics.
      </p>

      {selected && <ZipDrawer row={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function ZipDrawer({ row, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="coverage-zip-drawer-backdrop">
      <div className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="coverage-zip-drawer">
        <div className="px-6 sm:px-8 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#C75D33]">ZIP coverage</div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-0.5 font-mono">{row.zip_code}</h2>
            <div className="text-sm text-slate-500 mt-0.5">{row.city_name || "—"}{row.state ? " · " + row.state : ""}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="coverage-zip-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 sm:px-8 py-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">All bidders · ordered by tier weight (winner first)</div>
          <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
            {row.bidders.map((b, i) => (
              <li key={b.company_id} className="px-4 py-3 flex items-center gap-3" data-testid={`coverage-zip-bidder-${b.company_id}`}>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 w-6">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{b.business_name}</div>
                  <div className="text-[11px] text-slate-500">
                    via {b.source === "custom" ? "custom ZIP" : "city coverage"} · min ${(b.quote_minimum_cents / 100).toFixed(0)} quote
                  </div>
                </div>
                <TierPill tier={b.tier} />
                <div className="text-right shrink-0">
                  <div className={`font-bold text-sm tabular-nums ${b.wallet_low ? "text-rose-600" : "text-slate-900"}`}>
                    <Wallet className="w-3 h-3 inline mr-0.5" /> {fmt$(b.wallet_balance_cents)}
                  </div>
                  {b.wallet_low && <div className="text-[9px] font-bold text-rose-600 uppercase">Low balance</div>}
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
            The bidder in row #1 wins the next eligible lead for this ZIP. A lead is only routed if the winner's wallet covers their tier charge ({pct(row.bidders[0]?.percent || 0)} of the quote, capped $10–$50) — otherwise the next-best eligible bidder takes it.
          </p>
        </div>
      </div>
    </div>
  );
}

// ----------------- BY COMPANY -----------------
function ByCompanyTab() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/coverage/by-company")
      .then(({ data }) => setCompanies(data.companies || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load"))
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => ({
    companies: companies.length,
    avg_zips: companies.length ? Math.round(companies.reduce((s, c) => s + c.zip_count, 0) / companies.length) : 0,
    total_wallets: companies.reduce((s, c) => s + (c.wallet_balance_cents || 0), 0),
    weighted_pct: companies.length
      ? companies.reduce((s, c) => s + c.percent * c.zip_count, 0) / Math.max(1, companies.reduce((s, c) => s + c.zip_count, 0))
      : 0,
  }), [companies]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Bidding companies" value={totals.companies} />
        <Stat label="Avg ZIPs / co." value={totals.avg_zips} />
        <Stat label="Total wallets" value={fmt$(totals.total_wallets)} accent="text-emerald-600" />
        <Stat label="Avg bid (zip-weighted)" value={pct(totals.weighted_pct)} accent="text-[#C75D33]" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Company</th>
                <th className="text-left px-5 py-3 font-bold">Tier · bid</th>
                <th className="text-center px-5 py-3 font-bold">ZIPs</th>
                <th className="text-left px-5 py-3 font-bold">Cities</th>
                <th className="text-right px-5 py-3 font-bold">Wallet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…</td></tr>
              ) : companies.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-slate-500" data-testid="coverage-by-company-empty">No companies have enabled marketplace yet.</td></tr>
              ) : companies.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(c)} data-testid={`coverage-by-company-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{c.business_name}</div>
                    <div className="text-xs text-slate-500">{c.slug}</div>
                  </td>
                  <td className="px-5 py-3"><TierPill tier={c.tier} /></td>
                  <td className="px-5 py-3 text-center font-bold text-slate-900">{c.zip_count}</td>
                  <td className="px-5 py-3 text-xs text-slate-700">
                    {c.city_slugs.length ? c.city_slugs.length + " cities" : "—"}
                    {c.custom_zips.length ? <span className="text-slate-400"> · {c.custom_zips.length} custom ZIP{c.custom_zips.length === 1 ? "" : "s"}</span> : null}
                  </td>
                  <td className="px-5 py-3 text-right font-bold tabular-nums">
                    <span className={c.wallet_balance_cents < c.wallet_low_threshold_cents ? "text-rose-600" : "text-slate-900"}>
                      {fmt$(c.wallet_balance_cents)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="coverage-company-drawer">
            <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between">
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#C75D33]">Company coverage</div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-0.5">{selected.business_name}</h2>
                <div className="flex items-center gap-2 mt-2"><TierPill tier={selected.tier} /><span className="text-xs text-slate-500">Wallet: {fmt$(selected.wallet_balance_cents)}</span></div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">All ZIPs ({selected.zip_count})</div>
              <div className="flex flex-wrap gap-1.5">
                {selected.all_zips.map((z) => (
                  <span key={z} className="font-mono text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-800">{z}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ----------------- DEMAND VS SUPPLY -----------------
function DemandTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/coverage/demand", { params: { days } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load demand"))
      .finally(() => setLoading(false));
  }, [days]);

  const rows = data?.rows || [];
  const sum = data?.summary || {};

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          {[30, 60, 90, 180].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`coverage-demand-range-${d}`}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold ${days === d ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >{d}d</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Uncovered ZIPs" value={sum.uncovered_zips ?? "—"} accent="text-rose-600" sub={`${sum.uncovered_lead_count || 0} leads lost`} />
        <Stat label="Weak coverage" value={sum.weak_zips ?? "—"} accent="text-amber-600" sub="1 bidder, low tier" />
        <Stat label="Strong coverage" value={sum.covered_zips ?? "—"} accent="text-emerald-600" />
        <Stat label="ZIPs with demand" value={sum.total_zips_with_demand ?? "—"} sub={`last ${days}d`} />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">ZIP</th>
                <th className="text-left px-5 py-3 font-bold">City · State</th>
                <th className="text-center px-5 py-3 font-bold">Leads ({days}d)</th>
                <th className="text-center px-5 py-3 font-bold">Orphan</th>
                <th className="text-right px-5 py-3 font-bold">Avg quote</th>
                <th className="text-left px-5 py-3 font-bold">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading demand…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-16 text-center text-slate-500" data-testid="coverage-demand-empty">No platform funnel demand in this window yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.zip_code} className="hover:bg-slate-50" data-testid={`coverage-demand-row-${r.zip_code}`}>
                  <td className="px-5 py-3 font-mono font-bold text-slate-900">{r.zip_code}</td>
                  <td className="px-5 py-3 text-slate-700">
                    <div className="font-semibold">{r.city_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r.state || ""}</div>
                  </td>
                  <td className="px-5 py-3 text-center font-bold text-slate-900 tabular-nums">{r.lead_count}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`font-bold tabular-nums ${r.orphan_count > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {r.orphan_count}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-bold tabular-nums text-slate-900">${Math.round(r.avg_quote)}</td>
                  <td className="px-5 py-3">
                    {r.status === "uncovered" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-100 text-rose-800"><AlertTriangle className="w-3 h-3" /> Uncovered</span>
                    ) : r.status === "weak" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Weak · {r.bidder_count} bidder</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"><TrendingUp className="w-3 h-3" /> {r.bidder_count} bidder{r.bidder_count > 1 ? "s" : ""} · {pct(r.top_percent)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        <strong className="text-rose-600">Uncovered</strong> = at least one homeowner quoted from this ZIP and we have no bidder. Best targets for invite emails. <strong className="text-amber-600">Weak</strong> = single bronze/silver bidder. <strong className="text-emerald-600">Strong</strong> = multiple bidders or gold/platinum tier — safe places to scale paid traffic.
      </p>
    </>
  );
}
